import { useState, type ReactNode } from 'react';
import { TextField } from '../ui';
import { useConsoleI18n } from './bind';
import { FormDialog, sizeOf } from './FormDialog';

// Create/edit-a-record dialogs: the form state with an id suggested from the name, the id field, and the dialog with
// the server error on top and Cancel / Save below.

/** Ids are lowercase slugs (DNS-label style). */
export const SLUG = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/;

/** `"Customer A – Prod"` → `customer-a-prod` (at most 63 characters). */
export function slugify(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/, '');
}

/**
 * The id of a record: typed (or suggested from the name) on create, read-only afterwards. `onChange`'s `touched`
 * tells the form to stop following the name once the user typed an id themselves.
 */
export function IdField({
  value,
  isNew,
  onChange,
  example,
  label,
}: {
  value: string;
  isNew: boolean;
  onChange: (id: string, touched: boolean) => void;
  example: string;
  label?: string;
}) {
  const { t } = useConsoleI18n();
  const invalid = isNew && !!value && !SLUG.test(value);
  return (
    <TextField
      label={label ?? t('editor.id')}
      value={value}
      readOnly={!isNew}
      disabled={!isNew}
      onChange={(e) => onChange(e.target.value, true)}
      error={invalid ? t('editor.idInvalid') : undefined}
      hint={isNew ? t('editor.idHintNew', { example }) : t('editor.idHint')}
      required
    />
  );
}

/**
 * Form state for a create/edit dialog: the value, the id suggested from the name while creating, and `run(save)`
 * with busy state and the server error kept inside the dialog (`formatError` turns it into words).
 */
export function useEditorForm<T extends { name: string }>(
  initial: T & { id: string },
  isNew: boolean,
  formatError: (e: unknown) => string = (e) => (e as Error)?.message || String(e)
) {
  const [v, setV] = useState(initial);
  const [idTouched, setIdTouched] = useState(!isNew || !!initial.id);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof (T & { id: string })>(k: K, value: (T & { id: string })[K]) =>
    setV((cur) => {
      const next = { ...cur, [k]: value };
      if (k === 'name' && isNew && !idTouched) next.id = slugify(String(value));
      return next;
    });
  const setId = (id: string, touched: boolean) => {
    setIdTouched(touched && !!id);
    setV((cur) => ({ ...cur, id }));
  };
  const run = async (save: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await save();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return { v, setV, set, setId, error, setError, busy, run };
}

/**
 * Dialog with the server error on top and Cancel / Save in the footer: a `FormDialog` (Enter saves, closing with
 * unsaved input asks first, a backdrop press never closes it, nothing closes while it saves).
 */
export function EditorDialog({
  title,
  error,
  busy,
  canSave,
  onSave,
  onClose,
  children,
  maxWidth = 680,
  saveLabel,
  'data-testid': testId,
}: {
  title: ReactNode;
  error: unknown;
  busy: boolean;
  canSave: boolean;
  onSave: () => void;
  onClose: () => void;
  children: ReactNode;
  maxWidth?: number;
  saveLabel?: ReactNode;
  'data-testid'?: string;
}) {
  const { t } = useConsoleI18n();
  return (
    <FormDialog
      title={title}
      size={sizeOf(maxWidth)}
      error={error || undefined}
      busy={busy}
      canSubmit={canSave}
      onSubmit={onSave}
      onClose={onClose}
      submitLabel={saveLabel ?? t('common.save')}
      data-testid={testId}
    >
      {children}
    </FormDialog>
  );
}
